import React, { useState, useEffect, Suspense } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { FileText, MessageSquare, Truck, RotateCcw } from "lucide-react";
import { ModernProductData } from "@/types/modernProductContent";
import { cn } from "@/lib/utils";
import { RadixErrorBoundary } from "@/components/error/RadixErrorBoundary";
import { useModernProductContent } from "@/hooks/useModernProductContent";
import DynamicProductContent from "./DynamicProductContent";
import ProductReviewsSection from "./ProductReviewsSection";
import DeliveryTab from "./DeliveryTab";
import ReturnsTab from "./ReturnsTab";
import { useEnhancedProductData } from "@/hooks/useEnhancedProductData";

interface ModernProductTabsProps {
  productId: string;
  className?: string;
}

const ModernProductTabs: React.FC<ModernProductTabsProps> = ({
  productId,
  className
}) => {
  const { data: productData, isLoading } = useModernProductContent(productId);
  const { data: enhancedProductData } = useEnhancedProductData(productId, false);
  const [activeTab, setActiveTab] = useState("info");

  if (isLoading) {
    return (
      <div className={cn("space-y-6", className)}>
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const reviews = enhancedProductData?.reviews || [];
  const averageRating = enhancedProductData?.average_rating || 0;
  const reviewCount = enhancedProductData?.review_count || reviews.length;

  return (
    <RadixErrorBoundary>
      <Suspense fallback={<Skeleton className="h-64 w-full" />}>
        <Card className={cn("relative bg-gradient-to-br from-background to-muted/10 border-border/50 shadow-lg", className)}>
          <CardContent className="relative z-10 p-0">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-4 rounded-none border-b bg-transparent p-0 h-auto">
                <TabsTrigger 
                  value="info" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent bg-transparent py-4 px-6 text-sm font-medium transition-all hover:bg-muted/50"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Product Info
                </TabsTrigger>
                <TabsTrigger 
                  value="delivery" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent bg-transparent py-4 px-6 text-sm font-medium transition-all hover:bg-muted/50"
                >
                  <Truck className="h-4 w-4 mr-2" />
                  Delivery
                </TabsTrigger>
                <TabsTrigger 
                  value="reviews" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent bg-transparent py-4 px-6 text-sm font-medium transition-all hover:bg-muted/50"
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Reviews
                </TabsTrigger>
                <TabsTrigger 
                  value="returns" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent bg-transparent py-4 px-6 text-sm font-medium transition-all hover:bg-muted/50"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Returns
                </TabsTrigger>
              </TabsList>

              <div className="p-6">
                <TabsContent value="info" className="mt-0">
                  {productData ? (
                    <DynamicProductContent product={productData} />
                  ) : (
                    <div className="text-center py-12">
                      <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
                      <h3 className="text-xl font-semibold text-muted-foreground mb-2">No Content Available</h3>
                      <p className="text-muted-foreground">This product's content is currently being updated.</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="reviews" className="mt-0">
                  <ProductReviewsSection
                     reviews={reviews}
                     averageRating={averageRating}
                     reviewCount={reviewCount}
                  />
                </TabsContent>


                <TabsContent value="delivery" className="mt-0">
                  <DeliveryTab />
                </TabsContent>

                <TabsContent value="returns" className="mt-0">
                  <ReturnsTab />
                </TabsContent>
              </div>
            </Tabs>
          </CardContent>
        </Card>
      </Suspense>
    </RadixErrorBoundary>
  );
};

export default ModernProductTabs;